/* eslint-disable @typescript-eslint/naming-convention */
import { ICartItemResponse, ITransactionOrder } from '../../IApiResponses';
import { ICartItem, SKU_TYPE } from '../../../domain/entities/Cart/CartItem';

type responseDataMapper = {
  cartItems: ICartItemResponse[];
  transactionOrder: ITransactionOrder;
};

const enhanceCartItem = (item: ICartItem, transactionOrder: ITransactionOrder): ICartItem => {
  if (item.skuType === SKU_TYPE.GiftWrap) {
    const { card_message: cardMessage } = transactionOrder;
    return { ...item, cardMessage, productRgnSubheading: null };
  }
  if (item.skuType === SKU_TYPE.Donation) {
    return { ...item, productSize: null };
  }
  return item;
};

const cartItemsMapper = {
  response({ cartItems, transactionOrder }: responseDataMapper): ICartItem[] {
    return cartItems.filter(item => item.sku_type).map((item): ICartItem => {
      const {
        applied_price: appliedPrice,
        applied_tax: appliedTax,
        discounted_price: discountedPrice,
        discounted_applied_price: discountedAppliedPrice,
        discounted_unit_price: discountedUnitPrice,
        estimated_delivery_date: estimatedDeliveryDate,
        formatted_applied_price: formattedAppliedPrice,
        formatted_discounted_applied_price: formattedDiscountedAppliedPrice,
        formatted_discounted_unit_price: formattedDiscountedUnitPrice,
        isHazmat: isHazmatNumber,
        isFreeVirtualSkuItem,
        isOutOfStock: isOutOfStockNumber,
        isKitItem: isKitItemNumber,
        kit_discounted_unit_price: kitDiscountedUnitPrice,
        kit_unit_price: kitUnitPrice,
        item_quantity: itemQuantity,
        small_image: smallImages,
        medium_image: mediumImages,
        large_image: largeImages,
        image_s: imageS,
        image_m: imageM,
        image_l: imageL,
        offer_code: offerCode,
        product_code: productCode,
        prod_base_id: productBaseId,
        prod_rgn_name: productRgnName,
        prod_rgn_subheading: productRgnSubheading,
        product_size: productSize,
        replenishment_item,
        replenishment_frequency: replenishmentFrequency,
        sku_base_id: skuBaseId,
        total_percent_discount: totalPercentDiscount,
        hex_value: hexValue,
        unit_price: unitPrice,
        formatted_unit_price: formattedUnitPrice,
        monogrammed_item,
        monogram_sku_discount_price: monogramSkuDiscountPrice,
        monogram_sku_price: monogramSkuPrice,
        monogram_sku_total_percent_discount: monogramSkuPercentDiscount,
        monogram_sku_formatted_price: monogramSkuFormattedPrice,
        monogram_discounted_unit_price: monogramDiscountedUnitPrice,
        monogram_formatted_unit_price: formattedMonogramPrice,
        monogram_total_percent_discount: monogramTotalPercentDiscount,
        monogram_unit_price: monogramUnitPrice,
        monogram_formatted_discounted_unit_price: formattedMonogramDiscountPrice,
        kit_cart_quantity: kitQuantity,
        kit_prod_rgn_names: kitNames,
        kit_formatted_discounted_unit_price: kitDiscountUnitPrice,
        isPreOrder: isPreOrderNumber,
        isSampleItem: isSampleItemNumber,
        sku_type: skuType,
        is_bopis_gwp: isBopisGWP,
        is_courier_gwp: isCourierGWP,
        is_gwp_offer: isGWPOffer,
        ...rest
      } = item;

      const isKitItem = Boolean(isKitItemNumber);
      const isMonogrammedItem = Boolean(monogrammed_item);
      const isMonogramItemDiscounted = Boolean(monogramSkuPercentDiscount);
      const isMonogramDiscounted = Boolean(monogramTotalPercentDiscount);
      const isOutOfStock = Boolean(isOutOfStockNumber);
      const isHazmat = Boolean(isHazmatNumber);
      const isAutoReplenish = Boolean(replenishment_item);
      const isComplimentaryGift = Boolean(isFreeVirtualSkuItem);
      const isPreOrder = Boolean(isPreOrderNumber);
      const isSampleItem = Boolean(isSampleItemNumber);
      const isBopisGWPItem = Boolean(isBopisGWP);
      const isCourierGWPItem = Boolean(isCourierGWP);
      const isGWPOfferItem = Boolean(isGWPOffer);

      const images = [
        mediumImages?.[0],
        imageM?.[0],
        smallImages?.[0],
        imageS?.[0],
        largeImages?.[0],
        imageL?.[0],
      ].filter(image => image);

      const mappedItem = {
        appliedPrice,
        appliedTax,
        discountedAppliedPrice,
        discountedPrice,
        discountedUnitPrice,
        estimatedDeliveryDate,
        formattedAppliedPrice,
        formattedDiscountedAppliedPrice,
        formattedDiscountedUnitPrice,
        formattedMonogramDiscountPrice,
        formattedMonogramPrice,
        formattedUnitPrice,
        hexValue,
        images,
        isAutoReplenish,
        isBopisGWPItem,
        isComplimentaryGift,
        isCourierGWPItem,
        isGWPOfferItem,
        isHazmat,
        isKitItem,
        isMonogramDiscounted,
        isMonogramItemDiscounted,
        isMonogrammedItem,
        isOutOfStock,
        isPreOrder,
        isSampleItem,
        itemQuantity,
        kitDiscountUnitPrice,
        kitDiscountedUnitPrice,
        kitNames,
        kitQuantity,
        kitUnitPrice,
        monogramDiscountedUnitPrice: monogramDiscountedUnitPrice || 0,
        monogramSkuDiscountPrice: monogramSkuDiscountPrice || 0,
        monogramSkuFormattedPrice,
        monogramSkuPercentDiscount,
        monogramSkuPrice: monogramSkuPrice || 0,
        monogramTotalPercentDiscount,
        monogramUnitPrice: monogramUnitPrice || 0,
        offerCode,
        productBaseId,
        productCode,
        productRgnName,
        productRgnSubheading,
        productSize,
        replenishmentFrequency,
        skuBaseId,
        skuType,
        totalPercentDiscount,
        unitPrice,
        ...rest,
      };

      return enhanceCartItem(mappedItem, transactionOrder);
    });
  },
};

export default cartItemsMapper;
